import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUiStore } from '@/stores/uiStore';

/**
 * Hook for managing filter state with bidirectional URL synchronization.
 * Filters sync between URL query parameters and Zustand UI store.
 */
export const usePlacesFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useUiStore((state) => state.filters);
  const setFilters = useUiStore((state) => state.setFilters);
  const clearFilters = useUiStore((state) => state.clearFilters);

  /**
   * Convert filter state to URL query parameters format.
   * Arrays become comma-separated strings, null values are omitted.
   */
  const filtersToURLParams = useCallback((filterState: typeof filters): Record<string, string> => {
    const params: Record<string, string> = {};

    if (filterState.search) {
      params.search = filterState.search;
    }
    if (filterState.type) {
      params.type = filterState.type;
    }
    if (filterState.country) {
      params.country = filterState.country;
    }
    if (filterState.rating !== null) {
      params.rating = filterState.rating.toString();
    }
    if (filterState.age_category.length > 0) {
      params.age_category = filterState.age_category.join(',');
    }
    if (filterState.tags.length > 0) {
      params.tags = filterState.tags.join(',');
    }
    if (filterState.kid_friendly === true) {
      params.kid_friendly = 'true';
    }
    if (filterState.disability_friendly === true) {
      params.disability_friendly = 'true';
    }

    return params;
  }, []);

  /**
   * Convert URL query parameters to filter state.
   * Comma-separated strings become arrays, boolean strings become booleans.
   */
  const urlParamsToFilters = useCallback((params: URLSearchParams): Partial<typeof filters> => {
    const filterState: Partial<typeof filters> = {};

    const search = params.get('search');
    if (search) filterState.search = search;

    const type = params.get('type');
    if (type) filterState.type = type;

    const country = params.get('country');
    if (country) filterState.country = country;

    const rating = params.get('rating');
    if (rating) {
      const ratingNum = parseInt(rating, 10);
      if (!isNaN(ratingNum)) filterState.rating = ratingNum;
    }

    const ageCategory = params.get('age_category');
    if (ageCategory) {
      filterState.age_category = ageCategory.split(',').filter(Boolean);
    }

    const tags = params.get('tags');
    if (tags) {
      filterState.tags = tags.split(',').filter(Boolean);
    }

    const kidFriendly = params.get('kid_friendly');
    if (kidFriendly === 'true') {
      filterState.kid_friendly = true;
    }

    const disabilityFriendly = params.get('disability_friendly');
    if (disabilityFriendly === 'true') {
      filterState.disability_friendly = true;
    }

    return filterState;
  }, []);

  /**
   * Sync filter state from URL to store on mount and URL changes (e.g., back button).
   * Only syncs if URL params differ from current filter state to avoid loops.
   */
  useEffect(() => {
    const urlFilters = urlParamsToFilters(searchParams);
    const currentFilters = filtersToURLParams(filters);
    const urlParamsObj = Object.fromEntries(searchParams);

    // Only update if URL params differ from current state (URL changed externally)
    if (JSON.stringify(urlParamsObj) !== JSON.stringify(currentFilters)) {
      // URL is source of truth - replace filters with URL values
      const mergedFilters = {
        search: urlFilters.search || '',
        type: urlFilters.type || null,
        country: urlFilters.country || null,
        rating: urlFilters.rating !== undefined ? urlFilters.rating : null,
        age_category: urlFilters.age_category || [],
        tags: urlFilters.tags || [],
        kid_friendly: urlFilters.kid_friendly !== undefined ? urlFilters.kid_friendly : null,
        disability_friendly: urlFilters.disability_friendly !== undefined ? urlFilters.disability_friendly : null,
      };
      setFilters(mergedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]); // React to URL changes (use toString() for stable comparison)

  /**
   * Sync filter state changes to URL (debounced to avoid excessive history entries).
   * Uses replace instead of push to avoid cluttering browser history.
   * Skips if URL is already in sync (prevents loops with URL→filters sync).
   */
  useEffect(() => {
    const urlParams = filtersToURLParams(filters);
    const currentParams = Object.fromEntries(searchParams);

    // Only update if filters changed and URL is different
    const urlParamsStr = JSON.stringify(urlParams);
    const currentParamsStr = JSON.stringify(currentParams);
    
    if (urlParamsStr !== currentParamsStr) {
      // Small delay to debounce rapid filter changes
      const timeoutId = setTimeout(() => {
        setSearchParams(urlParams, { replace: true });
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [filters, filtersToURLParams, searchParams, setSearchParams]);

  /**
   * Update a single filter value.
   */
  const updateFilter = useCallback(
    (key: keyof typeof filters, value: any) => {
      setFilters({ [key]: value } as Partial<typeof filters>);
    },
    [setFilters]
  );

  /**
   * Clear all filters and reset to defaults.
   */
  const handleClearFilters = useCallback(() => {
    clearFilters();
    setSearchParams({}, { replace: true });
  }, [clearFilters, setSearchParams]);

  return {
    filters,
    updateFilter,
    clearFilters: handleClearFilters,
    setFilters,
  };
};
